import { useEffect, useReducer, useRef, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { Temporal } from 'temporal-polyfill';

import { useLocationsDropdown } from '../../../../APIs/common';
import { useServicePeriodDropdown } from '../../../../APIs/CustomSetup/servicePeriod';
import { FilterDate } from '../../../../components/common/FilterToolbar/FilterToolbar';
import ClearFiltersButton from '../../../../components/common/ClearFiltersButton/ClearFiltersButton';
import NewBookingModal from '../../../bookingmanagement/components/AddBooking/NewBookingModal';
import ServicePeriodLegend from '../ServicePeriodLegend/ServicePeriodLegend';
import './calendarHeader.css';

const VIEW_LABELS = { day: 'Day', week: 'Week', 'month-grid': 'Month' };

/**
 * The calendar's whole header, in place of Schedule-X's own.
 *
 *   Line 1:  ‹            October 2026            ›
 *   Line 2:  [service periods]     [date] [Filters ▾] [Add Booking]
 *
 * It drives the calendar through the same $app state the library's own header
 * used (selected date, view), and talks to Calendar.jsx through the same window
 * events the old Location / Service Period dropdowns sent — so nothing that
 * fetches or colours bookings changes.
 */
export default function CalendarHeader({ $app }) {
  // Re-render whenever the calendar's date, view or range moves.
  const [, refresh] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    const signals = [$app.datePickerState.selectedDate, $app.calendarState.view, $app.calendarState.range];
    const unsubs = signals.map((s) => s.subscribe(() => refresh()));
    return () => unsubs.forEach((u) => u());
  }, [$app]);

  const selectedDate = $app.datePickerState.selectedDate.value;
  const viewName = $app.calendarState.view.value;
  const range = $app.calendarState.range.value;
  const views = $app.config.views.value;

  // ── Navigation ───────────────────────────────────────────────────────────
  const step = (direction) => {
    const view = views.find((v) => v.name === viewName);
    if (!view) return;
    const next = view.backwardForwardFn(selectedDate, direction * view.backwardForwardUnits);
    $app.datePickerState.selectedDate.value = next instanceof Temporal.ZonedDateTime ? next.toPlainDate() : next;
  };

  // "October 2026", or "Sep – Oct 2026" for a week across two months.
  const heading = (() => {
    const monthYear = (d) => d.toLocaleString('en-AU', { month: 'long', year: 'numeric' });
    if (viewName === 'week' && range?.start && range?.end) {
      const a = monthYear(range.start);
      const b = monthYear(range.end);
      if (a === b) return a;
      const sameYear = range.start.year === range.end.year;
      return sameYear
        ? `${range.start.toLocaleString('en-AU', { month: 'short' })} – ${range.end.toLocaleString('en-AU', { month: 'short' })} ${range.end.year}`
        : `${a} – ${b}`;
    }
    return selectedDate ? monthYear(selectedDate) : '';
  })();

  const setDate = (ymd) => {
    const today = Temporal.Now.plainDateISO($app.config.timezone.value);
    $app.datePickerState.selectedDate.value = ymd ? Temporal.PlainDate.from(ymd) : today;
  };

  const setView = (name) => {
    $app.calendarState.setView(name, $app.datePickerState.selectedDate.value);
  };

  // ── Location / service period (same events the old dropdowns sent) ──────
  const { locations } = useLocationsDropdown();
  const { names: servicePeriods } = useServicePeriodDropdown();
  const [location, setLocation] = useState(null);
  const [servicePeriod, setServicePeriod] = useState(null);

  const chooseLocation = (loc) => {
    if (!loc?.value) return;
    setLocation(loc);
    window.dispatchEvent(new CustomEvent('calendar-location-change', { detail: loc }));
  };

  const chooseServicePeriod = (sp) => {
    if (!sp?.value) return;
    setServicePeriod(sp);
    window.dispatchEvent(new CustomEvent('calendar-service-period-change', { detail: sp }));
  };

  const primaryLocation = (() => {
    const primary = locations?.find((l) => l.is_primary) || locations?.[0];
    return primary ? { value: primary.id, label: primary.name, isPrimary: primary.is_primary } : null;
  })();

  // Opens on the main location, as before.
  useEffect(() => {
    if (location || !primaryLocation) return;
    chooseLocation(primaryLocation);
  }, [locations, location]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Clear filters ────────────────────────────────────────────────────────
  // "Filtered" means anything moved off where the calendar opens: another
  // location, a service period, a view other than the first one, or a day
  // other than today. Clearing puts all four back.
  const initialView = useRef(viewName);
  const todayYmd = Temporal.Now.plainDateISO($app.config.timezone.value).toString();
  const hasFilters =
    Boolean(servicePeriod) ||
    Boolean(location && primaryLocation && location.value !== primaryLocation.value) ||
    viewName !== initialView.current ||
    (selectedDate && selectedDate.toString() !== todayYmd);

  const clearFilters = () => {
    if (servicePeriod) {
      setServicePeriod(null);
      window.dispatchEvent(new CustomEvent('calendar-service-period-change', { detail: null }));
    }
    if (primaryLocation && location?.value !== primaryLocation.value) chooseLocation(primaryLocation);
    if (viewName !== initialView.current) setView(initialView.current);
    setDate('');
  };

  // ── Filter panel ─────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const summary = [VIEW_LABELS[viewName], location?.label, servicePeriod?.label].filter(Boolean).join(' • ') || 'Filters';
  const activeCount = [location, servicePeriod].filter(Boolean).length;

  const option = (label, selected, onClick) => (
    <button key={label} type="button" className={`bm-fopt${selected ? ' is-selected' : ''}`} aria-pressed={selected} onClick={onClick}>
      <span className="bm-fopt-label">{label}</span>
      <span className="bm-fopt-check">
        <i className="ph ph-check" />
      </span>
    </button>
  );

  const [showAddBooking, setShowAddBooking] = useState(false);

  return (
    <div className="cal-head">
      {/* Line 1 */}
      <div className="cal-head-nav">
        <button type="button" className="cal-head-arrow" aria-label="Previous" onClick={() => step(-1)}>
          <i className="ph ph-caret-left" />
        </button>
        <h2 className="cal-head-title">{heading}</h2>
        <button type="button" className="cal-head-arrow" aria-label="Next" onClick={() => step(1)}>
          <i className="ph ph-caret-right" />
        </button>
      </div>

      {/* Line 2 */}
      <div className="cal-head-bar">
        <ServicePeriodLegend />

        <div className="cal-head-actions">
          <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />

          <FilterDate value={selectedDate ? selectedDate.toString() : ''} onChange={setDate} width={170} />

          <div ref={panelRef} className="bm-filter">
            <button
              type="button"
              className={`bm-filter-btn${activeCount ? ' is-active' : ''}${open ? ' is-open' : ''}`}
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
            >
              <i className="ph ph-funnel-simple bm-filter-icon" />
              <span className="bm-filter-text">{summary}</span>
              {activeCount > 0 && <span className="bm-filter-count">{activeCount}</span>}
              <i className="ph ph-caret-down bm-filter-caret" />
            </button>

            {open && (
              <div className="bm-filter-pop cal-head-pop" role="dialog" aria-label="Calendar filters">
                <Accordion alwaysOpen defaultActiveKey={['view']}>
                  <Accordion.Item eventKey="view">
                    <Accordion.Header>
                      <i className="ph ph-calendar-blank bm-fsec-icon" />
                      View
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="bm-fopts">
                        {views.map((v) => option(VIEW_LABELS[v.name] || v.label, v.name === viewName, () => setView(v.name)))}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>

                  <Accordion.Item eventKey="location">
                    <Accordion.Header>
                      <i className="ph ph-map-pin bm-fsec-icon" />
                      Location
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="bm-fopts">
                        {(locations || []).map((l) =>
                          option(l.name, location?.value === l.id, () =>
                            chooseLocation({ value: l.id, label: l.name, isPrimary: l.is_primary })
                          )
                        )}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>

                  <Accordion.Item eventKey="period">
                    <Accordion.Header>
                      <i className="ph ph-clock bm-fsec-icon" />
                      Service Period
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="bm-fopts">
                        {(servicePeriods || []).map((sp) =>
                          option(sp.name, servicePeriod?.value === sp.name, () => chooseServicePeriod({ value: sp.name, label: sp.name }))
                        )}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </div>
            )}
          </div>

          <button type="button" className="btn btn-primary cal-head-add" onClick={() => setShowAddBooking(true)}>
            Add Booking
          </button>
        </div>
      </div>

      {/* Booking Management's New Booking pop-up. A saved booking tells the
          calendar to reload (Calendar.jsx listens for the event). */}
      <NewBookingModal
        show={showAddBooking}
        onHide={() => setShowAddBooking(false)}
        onCreated={() => {
          setShowAddBooking(false);
          window.dispatchEvent(new CustomEvent('calendar-booking-added'));
        }}
      />
    </div>
  );
}
